import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function GoogleDriveBackup() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const load = async () => {
    setLoading(true);
    // Each call independent — Promise.all would blank everything if one fails
    try { const r = await api.get('/api/backup/drive/status'); setStatus(r.data); } catch { setStatus(null); }
    try {
      const r = await api.get('/api/backup/list');
      const allBackups = (r.data?.logs || r.data || []);
      setBackupHistory(
        allBackups
          .filter(b => b.backup_destination === 'drive' || b.destination === 'drive')
          .slice(0, 10)
      );
    } catch { setBackupHistory([]); }
    setLoading(false);
  };

  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const handleConnect = () => {
    window.location.href = '/api/backup/drive/auth';
  };

  const handleManualBackup = async () => {
    if (!window.confirm('Start a manual backup to Google Drive now?')) return;
    setBackingUp(true);
    try {
      await api.post('/api/backup/drive/backup');
      alert('Backup to Google Drive started successfully');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? Automated backups will stop.')) return;
    setDisconnecting(true);
    try {
      await api.post('/api/backup/drive/disconnect');
      alert('Google Drive disconnected');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleToggleActive = async () => {
    setTogglingActive(true);
    try {
      await api.put('/api/backup/drive/status', { is_active: !status?.is_active });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update setting');
    } finally {
      setTogglingActive(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isConnected = status?.connected;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Google Drive Backup</h1>
        <p className="text-gray-500 text-sm">Automated and manual database backups to Google Drive</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-400">
          Loading...
        </div>
      ) : (
        <>
          {/* Setup instructions — always show if not connected */}
          {!isConnected && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <span>⚙️</span> Setup Required
              </h3>
              <ol className="space-y-2 text-sm text-amber-700">
                <li className="flex items-start gap-2">
                  <span className="bg-amber-200 text-amber-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <span>Add <code className="bg-amber-100 px-1 rounded text-xs">GOOGLE_CLIENT_ID</code> and <code className="bg-amber-100 px-1 rounded text-xs">GOOGLE_CLIENT_SECRET</code> to your <code className="bg-amber-100 px-1 rounded text-xs">.env</code> file on the VPS.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-amber-200 text-amber-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <span>In Google Cloud Console, add <code className="bg-amber-100 px-1 rounded text-xs">https://reception.shahzadtex.com/api/backup/drive/callback</code> as an authorised redirect URI.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-amber-200 text-amber-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <span>Restart the backend container, then click <strong>Connect Google Drive</strong> below to authorise access.</span>
                </li>
              </ol>
            </div>
          )}

          {/* Connection status card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
                  isConnected ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isConnected ? '✅' : '❌'}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    {isConnected ? 'Connected to Google Drive' : 'Not Connected'}
                  </div>
                  {isConnected && status?.folder_name && (
                    <div className="text-sm text-gray-500 mt-0.5">
                      Folder: <span className="font-medium text-gray-700">{status.folder_name}</span>
                    </div>
                  )}
                  {isConnected && status?.last_backup_at && (
                    <div className="text-sm text-gray-500 mt-0.5">
                      Last backup: <span className="font-medium text-gray-700">{formatDate(status.last_backup_at)}</span>
                    </div>
                  )}
                  {isConnected && !status?.last_backup_at && (
                    <div className="text-sm text-gray-400">No backups yet</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {isConnected ? (
                  <>
                    {/* Active toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Auto-backup</span>
                      <button
                        onClick={handleToggleActive}
                        disabled={togglingActive}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                          status?.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        title={status?.is_active ? 'Click to disable auto-backup' : 'Click to enable auto-backup'}
                      >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          status?.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    <button
                      onClick={handleManualBackup}
                      disabled={backingUp}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {backingUp ? 'Backing up...' : '☁️ Backup Now'}
                    </button>
                    <button
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleConnect}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12 12-5.373 12-12S18.628 0 12 0zm5.82 8.527l-3.18 5.51H9.36L6.18 8.527h11.64zm-9.87 6.818l-1.59 2.755H4.18l1.59-2.755h2.18zm4.05 0h2.18l1.59 2.755H14.4l-1.59-2.755z"/>
                    </svg>
                    Connect Google Drive
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Backup history */}
          {isConnected && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">Backup History</h3>
                <p className="text-xs text-gray-400 mt-0.5">Last 10 Drive backups</p>
              </div>
              {backupHistory.length === 0 ? (
                <div className="p-10 text-center text-gray-400">
                  <div className="text-4xl mb-2">☁️</div>
                  <p>No Drive backups yet</p>
                  <p className="text-sm mt-1">Click "Backup Now" to create your first backup</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date & Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">File Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {backupHistory.map((b, i) => (
                        <tr key={b.id || i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {formatDate(b.created_at || b.backup_date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {b.file_name || b.filename || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatSize(b.file_size || b.size)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (b.status || 'success') === 'success'
                                ? 'bg-green-100 text-green-700'
                                : (b.status === 'failed' || b.status === 'error')
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {b.status || 'success'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 capitalize">
                            {b.backup_type || b.type || 'manual'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Footer note */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 px-5 py-3 flex items-center gap-2 text-sm text-gray-500">
            <span>🕑</span>
            <span>Automated daily backup runs at <strong>2:00 AM</strong> server time when auto-backup is enabled.</span>
          </div>
        </>
      )}
    </div>
  );
}
